import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Skeleton,
  Snackbar,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import RuleIcon from "@mui/icons-material/Rule";
import TuneIcon from "@mui/icons-material/Tune";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import HistoryIcon from "@mui/icons-material/History";
import { userActions } from "../../../actions/user.actions";
import { useCurrentUser } from "../../../hooks/useCurrentUser";
import ReviewPublishDialog from "./ReviewPublishDialog";
import VersionHistory from "./VersionHistory";
import { OP_WORDS, OutcomeChip } from "./ruleDisplay";

const POLICY_ID = 17;
const cellKey = (row) => `${row.nodeId}/${row.ruleId}`;

// One table line per threshold. Consecutive text values with the same operator and result (the State list) share a line
const toLines = (rows) =>
  rows.reduce((lines, row) => {
    const last = lines[lines.length - 1];
    if (typeof row.value === "string" && last?.texts && last.op === row.op && last.color === row.color) {
      last.texts.push(row.value);
    } else {
      lines.push(typeof row.value === "string" ? { op: row.op, color: row.color, texts: [row.value] } : { row });
    }
    return lines;
  }, []);

// Same look as the stat cards in Admin Settings > Zone Management
function StatCard({ icon, color, tint, label, value }) {
  return (
    <Box
      sx={{
        flex: "1 1 200px",
        display: "flex",
        alignItems: "center",
        gap: "14px",
        padding: "14px 20px",
        borderRadius: "14px",
        border: `1.5px solid ${color}33`,
        boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: "50%",
          backgroundColor: tint,
          color,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>{value}</Typography>
      </Box>
    </Box>
  );
}

function TableSkeleton({ columns }) {
  return (
    <TableBody>
      {Array.from({ length: 5 }).map((_, i) => (
        <TableRow key={i}>
          {Array.from({ length: columns }).map((_, j) => (
            <TableCell key={j}><Skeleton variant="text" /></TableCell>
          ))}
        </TableRow>
      ))}
    </TableBody>
  );
}

export default function RinuxRulesEditor() {
  const decode = useCurrentUser();
  const isAdmin = decode?.role === "admin";
  const dispatch = useDispatch();
  const { rules, isLoading, error, versions, versionsLoading, versionsError } = useSelector((state) => state.RinuxRules);
  const [activeTabKey, setActiveTabKey] = useState("thresholds");
  // Only the thresholds the admin typed in: "nodeId/ruleId" -> text in the field
  const [values, setValues] = useState({});
  const [reviewOpen, setReviewOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    if (!isAdmin) return;
    dispatch(userActions.fetchRinuxRules(POLICY_ID));
    dispatch(userActions.fetchRinuxRuleVersions(POLICY_ID));
  }, [dispatch, isAdmin]);

  if (!isAdmin) return null;

  const checks = rules?.checks || [];
  const editableChecks = checks.filter((check) => check.kind === "editable");
  const readOnlyChecks = checks.filter((check) => check.kind !== "editable");
  const thresholdCount = editableChecks.reduce(
    (count, check) => count + check.rows.filter((row) => typeof row.value === "number").length,
    0
  );
  const liveVersion = versions.find((version) => version.isCurrent);

  const isChanged = (row) => {
    const text = values[cellKey(row)];
    return text !== undefined && (text.trim() === "" || Number(text) !== row.value);
  };
  const isInvalid = (row) => {
    const text = values[cellKey(row)];
    return text !== undefined && (text.trim() === "" || !Number.isFinite(Number(text)));
  };
  const changes = editableChecks.flatMap((check) =>
    check.rows
      .filter((row) => typeof row.value === "number" && isChanged(row))
      .map((row) => ({ check, row, newValue: Number(values[cellKey(row)]) }))
  );
  const hasInvalid = changes.some(({ row }) => isInvalid(row));

  const handlePublished = (message) => {
    setReviewOpen(false);
    setValues({});
    setSnackbar({ open: true, message, severity: "success" });
  };

  const handleReverted = (message) => {
    setValues({});
    setSnackbar({ open: true, message, severity: "success" });
  };

  const renderThresholdLine = (check, line, index, lineCount) => {
    const { row } = line;
    const isNumber = row && typeof row.value === "number";
    const changed = isNumber && isChanged(row);

    let condition = OP_WORDS[row?.op] || row?.op;
    if (line.texts) condition = line.texts.length > 1 && line.op === "==" ? "Is one of" : OP_WORDS[line.op] || "Matches";
    else if (row.value === null) condition = "Otherwise";

    let threshold = <Box component="span" sx={{ color: "text.secondary" }}>—</Box>;
    if (line.texts) {
      threshold = (
        <Box sx={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
          {line.texts.map((text) => (
            <Chip key={text} size="small" label={text} />
          ))}
        </Box>
      );
    } else if (isNumber) {
      threshold = (
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <TextField
            size="small"
            type="number"
            value={values[cellKey(row)] ?? String(row.value)}
            onChange={(e) => setValues((current) => ({ ...current, [cellKey(row)]: e.target.value }))}
            error={isInvalid(row)}
            inputProps={{ step: "any", "aria-label": `${check.name} threshold` }}
            sx={{ width: 140 }}
          />
          {changed && (
            <Box component="span" sx={{ fontSize: 12, color: "text.secondary" }}>
              was {row.value}
            </Box>
          )}
        </Box>
      );
    }

    return (
      <TableRow key={`${check.name}-${index}`} sx={changed ? { backgroundColor: "#FFFAEB" } : undefined}>
        {index === 0 && (
          <TableCell rowSpan={lineCount} sx={{ verticalAlign: "top", borderRight: "1px solid rgba(224, 224, 224, 1)" }}>
            <Box sx={{ fontWeight: 500 }}>{check.name}</Box>
            <Box sx={{ fontSize: 12, color: "text.secondary" }}>{check.inputs[0]}</Box>
          </TableCell>
        )}
        <TableCell sx={row?.value === null ? { color: "text.secondary" } : undefined}>{condition}</TableCell>
        <TableCell>{threshold}</TableCell>
        <TableCell>
          <OutcomeChip color={line.color ?? row.color} />
        </TableCell>
      </TableRow>
    );
  };

  return (
    <div style={{ width: "100%", backgroundColor: "white", minHeight: "100vh" }}>
      <Box sx={{ padding: "24px" }}>
        <h2>RINUX Credit Rules</h2>
        <Typography variant="body2" color="text.secondary" sx={{ marginBottom: "20px" }}>
          Policy #{POLICY_ID} · only the threshold numbers of simple checks can be changed here
        </Typography>

        <Box sx={{ display: "flex", gap: "16px", marginBottom: "20px", flexWrap: "wrap" }}>
          <StatCard
            icon={<RuleIcon sx={{ fontSize: 22 }} />}
            color="#1D4ED8"
            tint="#EFF6FF"
            label="Total checks"
            value={rules ? checks.length : "—"}
          />
          <StatCard
            icon={<TuneIcon sx={{ fontSize: 22 }} />}
            color="#027A48"
            tint="#ECFDF3"
            label="Editable thresholds"
            value={rules ? thresholdCount : "—"}
          />
          <StatCard
            icon={<LockOutlinedIcon sx={{ fontSize: 22 }} />}
            color="#6D28D9"
            tint="#F5F3FF"
            label="Read-only checks"
            value={rules ? readOnlyChecks.length : "—"}
          />
          <StatCard
            icon={<HistoryIcon sx={{ fontSize: 22 }} />}
            color="#d97706"
            tint="#fffbeb"
            label="Live version"
            value={liveVersion ? `v${liveVersion.version}` : "—"}
          />
        </Box>

        <Tabs value={activeTabKey} onChange={(e, value) => setActiveTabKey(value)}>
          <Tab value="thresholds" label="Thresholds" disableRipple />
          <Tab value="readOnly" label="Read-only Checks" disableRipple />
          <Tab value="versions" label="Version History" disableRipple />
        </Tabs>

        <Box sx={{ padding: "24px 0" }}>
          {error && <Box sx={{ color: "error.main", marginBottom: "12px" }}>{error}</Box>}

          {activeTabKey === "thresholds" && (
            <>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "16px",
                  gap: "12px",
                  flexWrap: "wrap",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: hasInvalid ? "error.main" : changes.length ? "#B54708" : "text.secondary" }}
                >
                  {hasInvalid
                    ? "Enter a number in every changed threshold."
                    : changes.length
                    ? `${changes.length} threshold${changes.length > 1 ? "s" : ""} changed. Review them before publishing.`
                    : "No changes yet. Edit a threshold to start."}
                </Typography>
                <Box sx={{ display: "flex", gap: "8px" }}>
                  <Button onClick={() => setValues({})} disabled={!changes.length}>
                    Reset
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => setReviewOpen(true)}
                    disabled={!changes.length || hasInvalid}
                    sx={{ color: "#fff" }}
                  >
                    Review &amp; Publish
                  </Button>
                </Box>
              </Box>

              <Paper variant="outlined" sx={{ borderRadius: "8px" }}>
                <TableContainer>
                  <Table>
                    <TableHead sx={{ backgroundColor: "#F8F8F8" }}>
                      <TableRow>
                        <TableCell sx={{ width: "28%" }}>Check</TableCell>
                        <TableCell sx={{ width: "18%" }}>Condition</TableCell>
                        <TableCell>Threshold</TableCell>
                        <TableCell sx={{ width: "14%" }}>Result</TableCell>
                      </TableRow>
                    </TableHead>
                    {isLoading && !rules ? (
                      <TableSkeleton columns={4} />
                    ) : (
                      <TableBody>
                        {editableChecks.map((check) => {
                          const lines = toLines(check.rows);
                          return lines.map((line, index) => renderThresholdLine(check, line, index, lines.length));
                        })}
                      </TableBody>
                    )}
                  </Table>
                </TableContainer>
              </Paper>
            </>
          )}

          {activeTabKey === "readOnly" && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ marginBottom: "16px" }}>
                These checks use several inputs or are on/off flags, so they can't be changed here. A developer edits them.
              </Typography>
              <Paper variant="outlined" sx={{ borderRadius: "8px" }}>
                <TableContainer>
                  <Table>
                    <TableHead sx={{ backgroundColor: "#F8F8F8" }}>
                      <TableRow>
                        <TableCell sx={{ width: "30%" }}>Check</TableCell>
                        <TableCell sx={{ width: "15%" }}>Type</TableCell>
                        <TableCell>Inputs</TableCell>
                      </TableRow>
                    </TableHead>
                    {isLoading && !rules ? (
                      <TableSkeleton columns={3} />
                    ) : (
                      <TableBody>
                        {readOnlyChecks.map((check) => (
                          <TableRow key={check.name} hover>
                            <TableCell sx={{ fontWeight: 500 }}>{check.name}</TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                variant="fibePill"
                                color={check.kind === "complex" ? "secondary" : "default"}
                                label={check.kind === "complex" ? "Complex" : "Flag-only"}
                              />
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
                                {check.inputs.map((input) => (
                                  <Chip key={input} size="small" variant="outlined" label={input} />
                                ))}
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    )}
                  </Table>
                </TableContainer>
              </Paper>
            </>
          )}

          {activeTabKey === "versions" && (
            <VersionHistory
              policyId={POLICY_ID}
              versions={versions}
              loading={versionsLoading}
              error={versionsError}
              onReverted={handleReverted}
            />
          )}
        </Box>
      </Box>

      <ReviewPublishDialog
        open={reviewOpen}
        policyId={POLICY_ID}
        changes={changes}
        onClose={() => setReviewOpen(false)}
        onPublished={handlePublished}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
